import { useState, useEffect } from 'react';
import { Quiz, QuizQuestion, CreateQuizInput, CreateQuestionInput, QuestionType } from '../../../types/elearning';
import { elearningAPI } from '../../../services/elearningApi';
import QuestionEditor from './QuestionEditor';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Card from '../../ui/Card';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import QuizComponent from '../../elearning/QuizComponent';

interface QuizBuilderProps {
  moduleId: string;
  existingQuiz?: Quiz;
  onQuizChange: (quiz: Quiz | null) => void;
}

export default function QuizBuilder({ moduleId, existingQuiz, onQuizChange }: QuizBuilderProps) {
  const [quiz, setQuiz] = useState<Quiz | null>(existingQuiz || null);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Quiz settings state
  const [title, setTitle] = useState(existingQuiz?.title || 'Module Quiz');
  const [passingScore, setPassingScore] = useState(existingQuiz?.passingScore || 70);
  const [timeLimit, setTimeLimit] = useState<number | undefined>(existingQuiz?.timeLimit);

  useEffect(() => {
    const loadQuiz = async () => {
      if (existingQuiz) {
        setQuiz(existingQuiz);
        setTitle(existingQuiz.title);
        setPassingScore(existingQuiz.passingScore);
        setTimeLimit(existingQuiz.timeLimit);
      } else {
        // Try to fetch quiz if module has one but it wasn't passed
        try {
          const fetchedQuiz = await elearningAPI.fetchQuizByModuleId(moduleId, { forEdit: true });
          if (fetchedQuiz) {
            setQuiz(fetchedQuiz);
            setTitle(fetchedQuiz.title);
            setPassingScore(fetchedQuiz.passingScore);
            setTimeLimit(fetchedQuiz.timeLimit);
          }
        } catch (err) {
          // Quiz doesn't exist yet, that's fine
          console.log('No quiz found for module:', moduleId);
        }
      }
    };
    loadQuiz();
  }, [existingQuiz, moduleId]);

  const handleCreateQuiz = async () => {
    if (!title.trim()) {
      alert('Please enter a quiz title');
      return;
    }

    try {
      setSaving(true);
      const input: CreateQuizInput = {
        moduleId,
        title: title.trim(),
        passingScore,
        timeLimit: timeLimit || undefined,
      };

      const newQuiz = await elearningAPI.createQuiz(moduleId, input);
      setQuiz(newQuiz);
      onQuizChange(newQuiz);
    } catch (err) {
      console.error('Failed to create quiz:', err);
      alert('Failed to create quiz. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuizSettings = async () => {
    if (!quiz) return;

    try {
      setSaving(true);
      // For now, we'll update the local state
      // In production, this would call an update API
      const updatedQuiz: Quiz = {
        ...quiz,
        title: title.trim(),
        passingScore,
        timeLimit: timeLimit || undefined,
      };
      setQuiz(updatedQuiz);
      onQuizChange(updatedQuiz);
    } catch (err) {
      console.error('Failed to update quiz:', err);
      alert('Failed to update quiz. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuestion = async (input: CreateQuestionInput) => {
    if (!quiz || !editingQuestion) return;

    try {
      setSaving(true);
      
      // If question has an ID, update it; otherwise it's a new question
      if (editingQuestion.id && editingQuestion.id !== '') {
        // Update existing question
        const updatedQuestion = await elearningAPI.updateQuestion(editingQuestion.id, input);
        
        const updatedQuestions = quiz.questions.map((q) =>
          q.id === editingQuestion.id ? updatedQuestion : q
        );
        
        const updatedQuiz: Quiz = {
          ...quiz,
          questions: updatedQuestions,
        };
        setQuiz(updatedQuiz);
        onQuizChange(updatedQuiz);
      } else {
        // Add new question
        const newQuestion = await elearningAPI.addQuestion(quiz.id, input);
        const updatedQuiz: Quiz = {
          ...quiz,
          questions: [...quiz.questions, newQuestion],
        };
        setQuiz(updatedQuiz);
        onQuizChange(updatedQuiz);
      }
      
      // Clear form and prepare for next question
      setEditingQuestion({
        id: '',
        quizId: quiz.id,
        question: '',
        type: QuestionType.MULTIPLE_CHOICE,
        order: quiz.questions.length + 1,
        points: 1,
        createdAt: '',
        updatedAt: '',
      } as QuizQuestion);
    } catch (err) {
      console.error('Failed to update question:', err);
      alert('Failed to update question. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!quiz) return;
    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      await elearningAPI.deleteQuestion(questionId);
      
      const updatedQuestions = quiz.questions.filter((q) => q.id !== questionId);
      const updatedQuiz: Quiz = {
        ...quiz,
        questions: updatedQuestions,
      };
      setQuiz(updatedQuiz);
      onQuizChange(updatedQuiz);
    } catch (err) {
      console.error('Failed to delete question:', err);
      alert('Failed to delete question. Please try again.');
    }
  };

  const handleDeleteQuiz = () => {
    if (!confirm('Are you sure you want to delete this quiz? All questions will be lost.')) {
      return;
    }
    setQuiz(null);
    onQuizChange(null);
    setTitle('Module Quiz');
    setPassingScore(70);
    setTimeLimit(undefined);
  };

  // If no quiz exists, show creation form
  if (!quiz) {
    return (
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quiz Builder</h3>
          <p className="text-sm text-gray-600 mb-6">
            Create a quiz for this module. You can add questions after creating the quiz.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quiz Title *
              </label>
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Module Quiz"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Passing Score (%) *
                </label>
                <Input
                  type="number"
                  value={passingScore}
                  onChange={(e) => setPassingScore(parseInt(e.target.value) || 70)}
                  min={0}
                  max={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Limit (minutes, optional)
                </label>
                <Input
                  type="number"
                  value={timeLimit || ''}
                  onChange={(e) => setTimeLimit(e.target.value ? parseInt(e.target.value) : undefined)}
                  min={1}
                  placeholder="No limit"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4">
              <Button variant="primary" onClick={handleCreateQuiz} disabled={saving}>
                {saving ? 'Creating...' : 'Create Quiz'}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Quiz exists - show question management
  const sortedQuestions = [...quiz.questions].sort((a, b) => a.order - b.order);

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Quiz Builder</h3>
            <p className="text-sm text-gray-600 mt-1">
              {quiz.questions.length} {quiz.questions.length === 1 ? 'question' : 'questions'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              {showPreview ? 'Hide Preview' : 'Preview Quiz'}
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleDeleteQuiz}
              className="text-red-600 hover:text-red-700"
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              Delete Quiz
            </Button>
          </div>
        </div>

        {/* Quiz Settings */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">Quiz Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passing Score (%)
              </label>
              <Input
                type="number"
                value={passingScore}
                onChange={(e) => setPassingScore(parseInt(e.target.value) || 70)}
                min={0}
                max={100}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Limit (min)
              </label>
              <Input
                type="number"
                value={timeLimit || ''}
                onChange={(e) => setTimeLimit(e.target.value ? parseInt(e.target.value) : undefined)}
                min={1}
                placeholder="No limit"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="primary" size="sm" onClick={handleUpdateQuizSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Update Settings'}
            </Button>
          </div>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="mb-6">
            <QuizComponent quiz={quiz} />
          </div>
        )}

        {/* Question Editor */}
        {editingQuestion && (
          <div className="mb-6">
            <QuestionEditor
              question={editingQuestion}
              onSave={handleUpdateQuestion}
              onCancel={() => setEditingQuestion(null)}
            />
          </div>
        )}

        {/* Questions Section */}
        {!editingQuestion && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Questions</h4>
                <p className="text-xs text-gray-500 mt-1">
                  {sortedQuestions.length} {sortedQuestions.length === 1 ? 'question' : 'questions'} total
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setEditingQuestion({
                  id: '',
                  quizId: quiz.id,
                  question: '',
                  type: QuestionType.MULTIPLE_CHOICE,
                  order: quiz.questions.length + 1,
                  points: 1,
                  createdAt: '',
                  updatedAt: '',
                } as QuizQuestion)}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Question
              </Button>
            </div>
          </div>
        )}

        {/* Question List */}
        {!editingQuestion && sortedQuestions.length > 0 && (
          <div className="space-y-4">
            {sortedQuestions.map((question, index) => (
              <div
                key={question.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-semibold text-gray-700">
                        Q{index + 1}
                      </span>
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded font-medium">
                        {question.type.replace('_', ' ')}
                      </span>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                        {question.points} {question.points === 1 ? 'point' : 'points'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 mb-3 leading-relaxed">{question.question}</p>
                    {question.type === QuestionType.MULTIPLE_CHOICE && question.options && (
                      <ul className="text-xs text-gray-600 space-y-1.5 ml-4 mb-3">
                        {question.options.map((opt, optIndex) => (
                          <li key={optIndex} className={`flex items-center gap-2 ${opt.isCorrect ? 'text-green-700 font-medium' : ''}`}>
                            {opt.isCorrect && <span className="text-green-600">✓</span>}
                            <span>{opt.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {question.type === QuestionType.TRUE_FALSE && (
                      <p className="text-xs text-gray-600 ml-4 mb-3">
                        Correct answer: <span className="font-medium text-gray-900">{question.correctAnswer ? 'True' : 'False'}</span>
                      </p>
                    )}
                    {question.type === QuestionType.SHORT_ANSWER && question.answerKey && (
                      <p className="text-xs text-gray-600 ml-4 mb-3">
                        Answer key: <span className="font-medium text-gray-900">{question.answerKey}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setEditingQuestion(question)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      aria-label="Edit question"
                      title="Edit question"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label="Delete question"
                      title="Delete question"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!editingQuestion && sortedQuestions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="mb-4">No questions yet. Add your first question to get started.</p>
            <Button
              variant="primary"
              onClick={() => setEditingQuestion({
                id: '',
                quizId: quiz.id,
                question: '',
                type: QuestionType.MULTIPLE_CHOICE,
                order: 1,
                points: 1,
                createdAt: '',
                updatedAt: '',
              } as QuizQuestion)}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add First Question
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
